/**
 * Shared words and paths for the named labels screens (ADR-039).
 */
import type {
  VaultHolderMemberDto,
  VaultNamedLabelAccessEntryDto,
  VaultNamedLabelReaderDto,
  VaultNamedLabelRequestDto,
  VaultNamedLabelSummaryDto,
} from '@/lib/api';

export type WorkspaceTier = 'owner' | 'admin' | 'member' | 'viewer';

export const isPrivilegedTier = (tier: WorkspaceTier | null | undefined): boolean =>
  tier === 'owner' || tier === 'admin';

export function namedLabelsPath(holderId: string): string {
  return `/workspaces/${encodeURIComponent(holderId)}/labels`;
}

export function namedLabelDetailPath(holderId: string, labelId: string): string {
  return `${namedLabelsPath(holderId)}/${encodeURIComponent(labelId)}`;
}

export function workspaceGroupsPath(holderId: string): string {
  return `/workspaces/${encodeURIComponent(holderId)}/groups`;
}

export function workspaceGroupDetailPath(holderId: string, groupId: string): string {
  return `${workspaceGroupsPath(holderId)}/${encodeURIComponent(groupId)}`;
}

/** A person by name, falling back to their handle, then an anonymous suffix. */
export function personLabel(
  members: readonly VaultHolderMemberDto[] | undefined,
  principalId: string,
  myPrincipalId?: string | null,
): string {
  if (myPrincipalId && principalId === myPrincipalId) return 'You';
  const member = members?.find((m) => m.principalId === principalId);
  if (member?.displayName) return member.displayName;
  if (member?.username) return `@${member.username}`;
  return `someone (…${principalId.slice(-6)})`;
}

/** Which labelled documents a reader can open, from their workspace role. */
export function tierReach(tier: VaultNamedLabelReaderDto['tier']): string {
  switch (tier) {
    case 'viewer':
      return 'Standard documents only';
    case 'member':
      return 'Standard and Confidential documents';
    default:
      return 'Documents at every tier';
  }
}

export const TIER_NAMES: Record<WorkspaceTier, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

/** The label's name when the caller can see it, otherwise "a label". */
export function labelName(
  labels: readonly VaultNamedLabelSummaryDto[] | undefined,
  labelId: string | null,
): string {
  if (!labelId) return 'a label';
  return labels?.find((l) => l.labelId === labelId)?.name ?? 'a label';
}

/**
 * What a request asks for, in plain words. A `group_member` request names the
 * person added and the group when the caller could resolve them (`groupMember`).
 */
export function describeRequest(
  request: VaultNamedLabelRequestDto,
  labels: readonly VaultNamedLabelSummaryDto[] | undefined,
  requester: string,
  groupMember?: { person: string | null; groupName: string | null },
): string {
  const label = labelName(labels, request.labelId);
  switch (request.kind) {
    case 'group_member': {
      const person = groupMember?.person ?? 'A group member';
      const group = groupMember?.groupName;
      return `${person}, added to ${group ? `the group ${group}` : 'a group'} by ${
        requester === 'You' ? 'you' : requester
      }, wants to read ${label}.`;
    }
    case 'remove_label':
      return `${requester} asked to take ${label} off a document.`;
    case 'move_label':
      return `${requester} asked to move a document from ${label} to ${labelName(labels, request.targetLabelId)}.`;
    case 'lower_tier':
      return `${requester} asked to lower a ${label} document to ${
        request.targetSensitivity === 'standard' ? 'Standard' : 'Confidential'
      }.`;
    case 'recovery':
      return `${requester} asked to add themselves to ${label}.`;
  }
}

/** "expires in 3 days" / "expires today" / "has expired". */
export function lapsesIn(lapsesAt: string, now: number = Date.now()): string {
  const ms = new Date(lapsesAt).getTime() - now;
  if (ms <= 0) return 'has expired';
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'expires today';
  return `expires in ${days} day${days === 1 ? '' : 's'}`;
}

export const ACCESS_ACTIONS: Record<VaultNamedLabelAccessEntryDto['action'], string> = {
  listed: 'saw it in a list',
  opened: 'opened its details',
  url_issued: 'was given a download link',
  bytes_streamed: 'was sent its contents',
};
